import React, {useState} from 'react';
import {createPortal} from 'react-dom';
import {Link, useLocation} from 'react-router-dom';

/**
 * Sous-menu Pannes, même patron que SidebarCarburantMenu.
 *
 * Même défaut que Carburant avant sa correction : ParametragePannes n'a ni bouton retour ni
 * entrée de menu — /pannes/parametrage était une impasse, accessible uniquement via le bouton
 * « Paramétrage » de l'écran de saisie. Contrairement à Carburant, Pannes n'a pas d'écran
 * « Rapports » séparé : le sous-menu ne compte donc que deux entrées.
 */

interface SidebarPannesMenuProps {
    isCollapsed: boolean;
}

const menuItems = [
    {id: 'saisie', title: 'Gestion des Pannes', icon: 'bi-cone-striped', path: '/pannes'},
    {id: 'parametrage', title: 'Paramétrage', icon: 'bi-gear', path: '/pannes/parametrage'},
];

const SidebarPannesMenu: React.FC<SidebarPannesMenuProps> = ({isCollapsed}) => {
    const location = useLocation();
    const [isExpanded, setIsExpanded] = useState(false);
    const [flyoutPos, setFlyoutPos] = useState<{ top: number; left: number } | null>(null);

    const isActive = (path: string) => location.pathname === path;
    const isAnyActive = () => menuItems.some(item => isActive(item.path));

    if (isCollapsed) {
        return (
            <div
                className="relative"
                onMouseEnter={(e) => {
                    const rect = e.currentTarget.getBoundingClientRect();
                    setFlyoutPos({top: rect.top, left: rect.right + 8});
                    setIsExpanded(true);
                }}
                onMouseLeave={() => setIsExpanded(false)}
            >
                {isAnyActive() && (
                    <span
                        className="absolute left-1.5 top-1/2 -translate-y-1/2 w-1 h-6 bg-emerald-400 rounded-full shadow-[0_0_8px_rgba(52,211,153,0.7)] z-10"/>
                )}
                <div
                    className={`group py-3 mx-3 my-0.5 rounded-xl flex items-center justify-center cursor-pointer transition-all duration-200 ${
                        isAnyActive()
                            ? 'bg-gradient-to-r from-emerald-400 to-green-500 text-white shadow-md shadow-emerald-900/30'
                            : 'text-white/80 hover:bg-white/10 hover:text-white'
                    }`}
                    onClick={() => setIsExpanded(!isExpanded)}
                >
                    <i className={`bi bi-cone-striped text-[17px] transition-transform duration-200 ${isAnyActive() ? '' : 'group-hover:scale-110'}`}></i>
                </div>

                {isExpanded && flyoutPos && createPortal(
                    <div
                        style={{position: 'fixed', top: flyoutPos.top, left: flyoutPos.left}}
                        onMouseEnter={() => setIsExpanded(true)}
                        onMouseLeave={() => setIsExpanded(false)}
                        className="w-60 rounded-xl bg-green-900/95 backdrop-blur-sm ring-1 ring-white/10 shadow-2xl shadow-black/40 py-2 z-[1100] animate-[fadeIn_0.15s_ease-out]">
                        <p className="px-4 pb-1.5 text-[11px] font-semibold uppercase tracking-wider text-emerald-300/70">Pannes</p>

                        {menuItems.map((item) => (
                            <Link
                                key={item.id}
                                to={item.path}
                                className={`flex items-center py-2 px-4 mx-2 rounded-lg no-underline text-[12.5px] transition-all duration-200 ${
                                    isActive(item.path)
                                        ? 'bg-white/15 text-white font-medium ring-1 ring-white/10'
                                        : 'text-white/65 hover:bg-white/10 hover:text-white/90'
                                }`}
                            >
                                <i className={`bi ${item.icon} mr-2 text-sm`}></i>
                                <span className="flex-1">{item.title}</span>
                            </Link>
                        ))}
                    </div>,
                    document.body
                )}
            </div>
        );
    }

    return (
        <div className="mb-1.5 relative">
            {isAnyActive() && (
                <span
                    className="absolute left-1.5 top-4 -translate-y-1/2 w-1 h-6 bg-emerald-400 rounded-full shadow-[0_0_8px_rgba(52,211,153,0.7)] z-10"/>
            )}

            <div
                className={`group py-2.5 px-4 mx-3 my-0.5 rounded-xl cursor-pointer flex items-center justify-between transition-all duration-200 ${
                    isAnyActive()
                        ? 'bg-gradient-to-r from-emerald-400 to-green-500 text-white shadow-md shadow-emerald-900/30'
                        : 'text-white/80 hover:bg-white/10 hover:text-white'
                }`}
                onClick={() => setIsExpanded(!isExpanded)}
            >
                <div className="flex items-center">
                    <i className={`bi bi-cone-striped text-[17px] mr-3 transition-transform duration-200 ${isAnyActive() ? '' : 'group-hover:scale-110'}`}></i>
                    <span className="text-[13px] font-medium tracking-wide">Pannes</span>
                </div>
                <i
                    className={`bi bi-chevron-down text-xs transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`}
                />
            </div>

            <div
                className={`overflow-hidden transition-all duration-300 ease-out ${isExpanded ? 'max-h-[400px] opacity-100' : 'max-h-0 opacity-0'}`}>
                <div className="mx-3 my-1 rounded-xl bg-black/15 ring-1 ring-white/5 backdrop-blur-sm py-2 space-y-0.5">
                    {menuItems.map((item) => (
                        <Link
                            key={item.id}
                            to={item.path}
                            className={`flex items-center py-2 px-3 mx-2 rounded-lg no-underline text-[12.5px] transition-all duration-200 ${
                                isActive(item.path)
                                    ? 'bg-white/15 text-white font-medium ring-1 ring-white/10'
                                    : 'text-white/65 hover:bg-white/10 hover:text-white/90'
                            }`}
                        >
                            <i className={`bi ${item.icon} mr-2 text-sm`}></i>
                            <span className="flex-1">{item.title}</span>
                        </Link>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default SidebarPannesMenu;
